/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import "./RealtimeSentimentAnalysis.css";
import { Spinner, Button, Badge } from "react-bootstrap";
import { BUILT_IN_TABS, SENTIMENT_TYPE } from "../../../../utility/constants";
import { useContactData } from "../../context/contactData";
import { Checkbox, List, ListItem, TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";

const getEventListRenderContent = (sentimentResults, categoriesToDisplay, bottomScrollRef) => {
	const messages = Object.values(sentimentResults.messages).filter((message) => categoriesToDisplay[message.category]);
	return (
		<>
			<List>
				{messages.map((message, index) => {
					const styling = message.IssuesDetected ? { backgroundColor: "whiteSmoke" } : {};
					return (
						<ListItem
							key={message.Id}
							style={{
								display: "block",
								borderTop: "1px solid lightGray",
								borderLeft: "1px solid lightGray",
								borderRight: "1px solid lightGray",
								borderBottom: index === messages.length - 1 ? "1px solid lightGray" : "",
								...styling,
							}}
						>
							<div style={{ display: "flex", justifyContent: "space-between" }}>
								<div className="d-flex w-100 align-items-center mb-2">
									<Badge variant="light" className="text-secondary p-2" style={{ fontSize: 12 }}>
										<span title={"Call time"} style={{ borderRight: "1px solid #ccc" }} className="mr-2 pr-2">
											{message.timeToDisplay}
										</span>
										<span title={"Participant"}>{message.ParticipantId}</span>
									</Badge>
								</div>
								<div>
									<Badge variant="light" className="text-secondary p-2" style={{ fontSize: 12 }}>
										{message.IssuesDetected && (
											<span
												title={"A potential call issue has been flagged here"}
												style={{ borderRight: "1px solid #ccc" }}
												className="mr-2 pr-2"
											>
												ISSUE
											</span>
										)}
										<span title={"Category"} style={{ borderRight: "1px solid #ccc" }} className="mr-2 pr-2">
											{message.category}
										</span>
										<span title={"Sentiment"} style={{ color: SENTIMENT_TYPE[message.Sentiment]?.color }}>
											{message.Sentiment}
										</span>
									</Badge>
								</div>
							</div>
							<div>
								<span className="text-secondary">{message.messageDisplayText}</span>
							</div>
						</ListItem>
					);
				})}
				<div ref={bottomScrollRef}></div>
			</List>
		</>
	);
};

const RenderContent = ({ sentimentResults, isLoading, error, categoriesToDisplay, bottomScrollRef }) => {
	if (isLoading) {
		return (
			<div style={{ margin: "auto", textAlign: "center" }}>
				<Spinner animation="border" className="m-2" role="status">
					<span className="sr-only">Loading...</span>
				</Spinner>
			</div>
		);
	}
	const message = error
		? error
		: !sentimentResults?.categories
		? "No results found. Please try to refresh in a few moments."
		: Object.keys(categoriesToDisplay).length === 0 && sentimentResults.categories
		? "Please select a category."
		: null;

	return message ? (
		<span style={{ margin: "auto", textAlign: "center" }}>{message}</span>
	) : (
		getEventListRenderContent(sentimentResults, categoriesToDisplay, bottomScrollRef)
	);
};

const GetLayout = ({
	refresh,
	sentimentResults,
	isLoading,
	error,
	setCategoriesToDisplay,
	categoriesToDisplay,
	bottomScrollRef,
}) => {
	return (
		<div
			style={{
				width: "100%",
				display: "flex",
				flexDirection: "column",
				flex: "1 1",
				height: "100%",
				position: "relative",
				padding: "5px 15px",
			}}
		>
			<div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16, marginTop: 10 }}>
				<div style={{ flex: "1 1", paddingRight: "20px" }}>
					{sentimentResults?.categories && (
						<div>
							<Autocomplete
								multiple
								id="sentiment-categories"
								options={Object.keys(sentimentResults?.categories || [])}
								onChange={(event, value) => {
									setCategoriesToDisplay(
										[...value].reduce((newObject, categoryName) => ({ ...newObject, [categoryName]: true }), {}),
									);
								}}
								disableCloseOnSelect
								getOptionSelected={(option, value) => {
									return option === value;
								}}
								getOptionLabel={(option) => `${option} (${sentimentResults.categories[option].length})`}
								defaultValue={Object.keys(sentimentResults?.categories || [])}
								renderOption={(option, { selected }) => (
									<>
										<Checkbox
											icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
											checkedIcon={<CheckBoxIcon fontSize="small" />}
											style={{ marginRight: 8 }}
											checked={selected}
										/>
										{`${option} (${sentimentResults.categories[option].length})`}
									</>
								)}
								renderInput={(params) => <TextField {...params} label="Categories" />}
							/>
						</div>
					)}
				</div>
				<div style={{ display: "flex", justifyContent: "flex-end", flexDirection: "column" }}>
					<div style={{ display: "flex", justifyContent: "space-between", alignContent: "flex-end" }}>
						{sentimentResults?.customerSentimentAverage ? (
							<div className="text-secondary" style={{ fontSize: 26, marginRight: "10px", fontWeight: "bolder" }}>
								<span style={{ color: sentimentResults?.customerSentimentAverage.color }} title={"Average Sentiment"}>
									<i
										className={sentimentResults?.customerSentimentAverage.icon}
										title={sentimentResults?.customerSentimentAverage.title}
									/>
								</span>
							</div>
						) : (
							<div />
						)}
						<div style={{ display: "flex", justifyContent: "flex-end", flexDirection: "column" }}>
							<Button onClick={refresh} size="sm" variant="outline-secondary">
								<i className="fas fa-sync"></i> Refresh
							</Button>
						</div>
					</div>
				</div>
			</div>

			<div
				style={{
					width: "100%",
					height: "100%",
					display: "flex",
					flexDirection: "column",
					flex: "1 1",
					position: "relative",
					overflow: "auto",
				}}
			>
				<RenderContent
					sentimentResults={sentimentResults}
					isLoading={isLoading}
					error={error}
					categoriesToDisplay={categoriesToDisplay}
					bottomScrollRef={bottomScrollRef}
				/>
			</div>
		</div>
	);
};

/**
 * This component retrieves and displays the realtime sentiment analysis results for a contact based on their contactId.
 * @param {Object} contact This object contains information about the contact such as their contactId.
 * @param {Boolean} activeTab The id of the currently active tab.
 */
export const RealtimeSentimentAnalysis = ({ contact, activeTab, enabled }) => {
	const { contactDataState, getSentimentAnalysisResults } = useContactData();
	const [categoriesToDisplay, setCategoriesToDisplay] = useState(undefined);
	const [loaded, setLoaded] = useState(false);
	const {
		loading = false,
		err = "",
		sentimentResults = {},
	} = contactDataState[contact.contactId]?.sentimentResults || {};

	const bottomScrollRef = useRef(null);

	const scrollToBottom = useCallback(() => {
		if (bottomScrollRef?.current) {
			bottomScrollRef.current.scrollIntoView({ block: "end", behavior: "smooth" });
		}
	}, []);

	useEffect(() => {
		if (enabled && activeTab === BUILT_IN_TABS.connect_realtime_sentiment_analysis.id && !loaded) {
			getSentimentAnalysisResults(contact).then(() => {
				scrollToBottom();
			});
		} else if (activeTab !== BUILT_IN_TABS.connect_realtime_sentiment_analysis.id) {
			setLoaded(false);
		}
	}, [activeTab, loaded, contact, getSentimentAnalysisResults, scrollToBottom, enabled]);

	const refresh = () => {
		getSentimentAnalysisResults(contact).then(() => {
			scrollToBottom();
		});
		setCategoriesToDisplay(undefined);
	};

	return enabled ? (
		<GetLayout
			refresh={refresh}
			sentimentResults={sentimentResults}
			setCategoriesToDisplay={setCategoriesToDisplay}
			categoriesToDisplay={categoriesToDisplay || sentimentResults.categories}
			contact={contact}
			isLoading={loading}
			error={err}
			bottomScrollRef={bottomScrollRef}
		/>
	) : (
		<div style={{ margin: "auto", textAlign: "center" }}>Not available for current contact</div>
	);
};
